import React from "react";

import { Picto } from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";

import { Dot } from "@/components/atoms/Dot/Dot";

import { calculateDuration } from "@/utils/UTasks";

import {
	VIEW_SEARCH_GROUP,
	PROJECT_SEARCH_GROUP,
	TASK_SEARCH_GROUP,
} from "@/constants/CSearch";

import {
	TViewSearchResultOptions,
	TProjectSearchResultOptions,
	TSelectedSearchOption,
	TTask,
	TTaskSearchResultOptions,
} from "@/types";

export interface ISearchResultRowProps {
	data: TSelectedSearchOption;
	onSelect: (value: TSelectedSearchOption) => void;
}

/**
 * One hit of the search page: a tile on the left, the name and a line of
 * context under it, a figure on the right. The whole row is the button.
 */
export const SearchResultRow: React.FC<ISearchResultRowProps> = ({
	data,
	onSelect,
}) => {
	let content: React.ReactNode = null;
	if (data.group === TASK_SEARCH_GROUP)
		content = <TaskRow data={data as TTaskSearchResultOptions} />;
	else if (data.group === PROJECT_SEARCH_GROUP)
		content = <ProjectRow data={data as TProjectSearchResultOptions} />;
	else if (data.group === VIEW_SEARCH_GROUP)
		content = <CalendarRow data={data as TViewSearchResultOptions} />;

	return (
		<button
			type="button"
			onClick={() => onSelect(data)}
			className="flex w-full items-center gap-3.5 rounded-lg px-3 py-2.5 text-left hover:bg-neutral-100"
		>
			{content}
		</button>
	);
};

const Tile: React.FC<{ color?: string | null; children: React.ReactNode }> = ({
	color,
	children,
}) => (
	<span
		className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-neutral-100"
		style={color ? { backgroundColor: `${color}1a` } : undefined}
	>
		{children}
	</span>
);

const TaskRow: React.FC<{ data: TTaskSearchResultOptions }> = ({ data }) => {
	const day = format(new Date(data.startDate), t("dates.dateFormat"));
	// Only a finished task has a length: an ongoing one would count up to
	// now, which is not what a search hit is about.
	const duration = data.endDate
		? calculateDuration([data as TTask]).duration
		: null;

	return (
		<>
			<Tile>
				<Picto
					icon="checkCircle"
					className="h-4 w-4 text-neutral-500"
				/>
			</Tile>
			<span className="flex min-w-0 grow flex-col gap-0.5">
				<span className="flex items-center gap-2">
					<span className="truncate text-sm font-normal">
						{data.label}
					</span>
					{data.project && (
						<span
							className="max-w-40 shrink-0 truncate rounded-full px-2.5 py-0.5 text-xs text-white"
							style={{ backgroundColor: data.project.color }}
						>
							{data.project.name}
						</span>
					)}
				</span>
				<span className="flex items-center gap-1.5 text-xs text-neutral-500">
					{data.client && (
						<>
							<Dot
								square
								color={data.client.color}
								className="h-2 w-2 min-w-2"
							/>
							<span className="truncate">{data.client.name}</span>
							<span>·</span>
						</>
					)}
					<span>{day}</span>
				</span>
			</span>
			{duration && (
				<span className="shrink-0 text-sm text-neutral-600">
					{duration}
				</span>
			)}
		</>
	);
};

const ProjectRow: React.FC<{ data: TProjectSearchResultOptions }> = ({
	data,
}) => (
	<>
		<Tile color={data.color}>
			<Dot color={data.color} className="h-2.5 w-2.5 min-w-2.5" />
		</Tile>
		<span className="flex min-w-0 grow flex-col gap-0.5">
			<span className="truncate text-sm font-normal">{data.label}</span>
			{data.client && (
				<span className="truncate text-xs text-neutral-500">
					{data.client.name}
				</span>
			)}
		</span>
		<Picto icon="chevron" className="h-4 w-4 shrink-0 text-neutral-400" />
	</>
);

const CalendarRow: React.FC<{ data: TViewSearchResultOptions }> = ({
	data,
}) => (
	<>
		<Tile color={data.color}>
			<Dot square color={data.color} className="h-3 w-3 min-w-3" />
		</Tile>
		<span className="flex min-w-0 grow flex-col gap-0.5">
			<span className="truncate text-sm font-normal">{data.label}</span>
			{data.description && (
				<span className="truncate text-xs text-neutral-500">
					{data.description}
				</span>
			)}
		</span>
		<Picto icon="chevron" className="h-4 w-4 shrink-0 text-neutral-400" />
	</>
);
